import { getReferrerAttribution } from "@outschool/attribution";
import { useLookupIP } from "@outschool/iplookup-client";
import { useLocalStorageState } from "@outschool/local-storage";
import { signUpPath } from "@outschool/routes";
import { logException } from "@outschool/sentry/dist/client";
import { guessBrowserTimeZone } from "@outschool/time";
import { useAnalytics, useTrackEvent } from "@outschool/ui-analytics";
import {
  ContinueWithGoogleOneTap,
  useLoginWithGoogleOneTapMutation,
  useSession
} from "@outschool/ui-auth";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import { GOOGLE_CLIENT_ID, isProduction } from "../../shared/Env";
import ActionType from "../actions/ActionType";
import useExperiment from "../hooks/useExperiment";
import { useAppState } from "../stores/AppStateProvider";

function useLoginWithGoogleOneTap({
  onSuccess,
  onError
}: {
  onSuccess: (sessionToken: string, isNewUser: boolean) => void;
  onError: (error?: Error) => void;
}) {
  const loginWithGoogle = useLoginWithGoogleOneTapMutation();

  const analytics = useAnalytics();
  const { ipInfoLoaded, isInGDPR } = useLookupIP();

  return React.useCallback(
    async (token: string) => {
      const createOptions = {
        attribution: await analytics.attribution(),
        browserTimeZone: guessBrowserTimeZone(),
        isGiftCardSignup: false,
        isLeader: false,
        subscribe: ipInfoLoaded ? !isInGDPR : false
      };

      const anonymousId = await analytics.anonymousId();
      const osRef = {
        ...getReferrerAttribution(),
        anonymousId
      };

      try {
        const { data } = await loginWithGoogle({
          variables: {
            createOptions,
            osRef,
            idToken: token
          }
        });

        if (data === undefined) {
          onError(new Error("empty response"));
          return;
        }

        if (data.loginOrCreateAccountWithGoogle.__typename === "LoginError") {
          onError(new Error("login error"));
        } else {
          const {
            loginOrCreateAccountWithGoogle: {
              isNewUser,
              authentication: { sessionToken }
            }
          } = data;
          onSuccess(sessionToken, isNewUser);
        }
      } catch (e) {
        onError(e);
      }
    },
    [analytics, loginWithGoogle, onSuccess, onError, ipInfoLoaded, isInGDPR]
  );
}

const ExperimentSignupWithGoogleOneTap = ({}) => {
  const navigate = useNavigation();
  const appState = useAppState();
  const trackEvent = useTrackEvent();
  const location = useLocation();
  const { variant, trigger } = useExperiment("GoogleOneTap");
  const { isLoggedIn } = useSession();

  const [overrideOneTap] = useLocalStorageState("override_one_tap", false);
  const isExperimentOn = variant === "treatment" || overrideOneTap;
  const loginWithGoogle = useLoginWithGoogleOneTap({
    onSuccess: (sessionToken, isNewUser) => {
      isNewUser
        ? trackEvent("signup-by-google-onetap")
        : trackEvent("signin-by-google-onetap");
      appState.appDispatcher.dispatch(ActionType.User.LOGGED_IN, {
        sessionToken
      });
    },
    onError: error => {
      logException(error);
      navigate(signUpPath());
    }
  });

  const shouldHide = React.useCallback(
    () =>
      /login|signup/.test(location.pathname) || /signup/.test(location.search),
    [location]
  );

  if (isLoggedIn) {
    return null;
  }

  return (
    <ContinueWithGoogleOneTap
      // eslint-disable-next-line i18next/no-literal-string
      clientId={GOOGLE_CLIENT_ID as string}
      loginWithGoogle={loginWithGoogle}
      shouldHide={shouldHide}
      logException={logException}
      onLoad={() => {
        trigger();
      }}
      shouldPrompt={isExperimentOn}
      onPrompt={notification => {
        if (!isProduction) {
          console.debug(
            // eslint-disable-next-line i18next/no-literal-string
            "onetap: notdisplayed: ",
            notification.getNotDisplayedReason()
          );
          // eslint-disable-next-line i18next/no-literal-string
          console.debug("onetap: skipped: ", notification.getSkippedReason());
          console.debug(
            // eslint-disable-next-line i18next/no-literal-string
            "onetap: dismissed: ",
            notification.getDismissedReason()
          );
        }
        trackEvent("one-tap-prompt", {
          isDisplayed: notification.isDisplayed(),
          notDisplayReason: notification.getNotDisplayedReason(),
          skippedReason: notification.getSkippedReason(),
          getDismissedReason: notification.getDismissedReason()
        });
      }}
    />
  );
};

export default React.forwardRef(ExperimentSignupWithGoogleOneTap);
